/**
 * Visits (P2 design §4.5): one `visit_id` per stretch of activity by one user in one
 * organisation, shared by every tab of the browser.
 *
 * - State lives in localStorage under a key scoped to the user (`user.sub`) and org:
 *   `{visit_id, started_at, last_activity_at}` (wall clock, `Date.now`, since it is compared
 *   across tabs and reloads). An identity change mints a new visit before anything else is
 *   stamped, so one visit never spans two identities.
 * - Activity updates `last_activity_at` at most every `VISIT_WRITE_MS`. Activity after more
 *   than `VISIT_IDLE_MS` without any (in any tab) mints a new visit.
 * - Minting: the tab stamps a provisional id at once, then takes a Web Lock and re-reads the
 *   state. If another tab minted meanwhile it adopts that visit and emits
 *   `visit.merge {from, to}`; otherwise it writes its own and emits `visit.start`. Without
 *   Web Locks the tab writes at once, and of two ids minted within `VISIT_RACE_MS` the
 *   lexically smaller wins on the next read; the loser emits `visit.merge`.
 * - The `storage` event tells the other tabs, which adopt the new visit.
 */

/** Activity gap after which the next activity starts a new visit. */
export const VISIT_IDLE_MS = 30 * 60_000;
/** Most frequent `last_activity_at` write. */
export const VISIT_WRITE_MS = 10_000;
/** Two visits minted this close together are one race, settled by the tie-break. */
export const VISIT_RACE_MS = 10_000;
/** localStorage key prefix; the user and org follow. */
export const VISIT_KEY_PREFIX = "sooth.telemetry.visit:";

/** The persisted visit state. */
export interface VisitState {
  visit_id: string;
  started_at: number;
  last_activity_at: number;
}

/** How a visit began, for `visit.start`. */
export interface VisitEntry {
  /** Route template of the page the visit started on, or null. */
  route: string | null;
  /** direct, alert, copy_link or link (a `?forecast=` deep or share link). */
  source: string;
}

/** Why a visit was minted. */
export type VisitReason = "load" | "idle" | "identity";

type StorageLike = Pick<Storage, "getItem" | "setItem">;

interface LockManagerLike {
  request(name: string, callback: () => Promise<void> | void): Promise<unknown>;
}

interface StorageEventLike {
  key: string | null;
  newValue: string | null;
}

interface WindowLike {
  addEventListener(type: "storage", listener: (e: StorageEventLike) => void): void;
  removeEventListener(type: "storage", listener: (e: StorageEventLike) => void): void;
}

export interface VisitsDeps {
  /** Records `visit.start` / `visit.merge`. */
  record: (event: Record<string, unknown>) => void;
  /** Called with the visit id to stamp (null while there is none), before any visit event. */
  onVisit: (visitId: string | null) => void;
  /** The entry route and source of this page load; used by the first visit it starts. */
  entry: () => VisitEntry;
  storage?: StorageLike | null;
  locks?: LockManagerLike | null;
  win?: WindowLike | null;
  wallNow?: () => number;
  newId?: () => string;
}

export interface Visits {
  /**
   * Apply the authenticated identity; a change starts (or adopts) that identity's visit.
   * `switched` says the identity changed before this instance knew it, so a new visit starts.
   */
  setIdentity(user: string | null, org: string | null, opts?: { switched?: boolean }): void;
  /** The user did something (presence activity or an intent). Cheap when nothing is due. */
  activity(): void;
  /** Detach; the stamped id is cleared. */
  stop(): void;
  readonly visitId: string | null;
}

function defaultNewId(): string {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function parseState(raw: string | null): VisitState | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<VisitState>;
    if (
      typeof v?.visit_id === "string" &&
      v.visit_id &&
      Number.isFinite(v.started_at) &&
      Number.isFinite(v.last_activity_at)
    ) {
      return v as VisitState;
    }
  } catch {
    /* corrupt: treated as absent */
  }
  return null;
}

/** The localStorage key of a user/org pair. */
export function visitKey(user: string, org: string | null): string {
  return `${VISIT_KEY_PREFIX}${encodeURIComponent(user)}|${encodeURIComponent(org ?? "")}`;
}

/** Create the visits tracker; nothing is stamped until `setIdentity` names a user. */
export function createVisits({
  record,
  onVisit,
  entry,
  storage = null,
  locks = null,
  win = null,
  wallNow = () => Date.now(),
  newId = defaultNewId,
}: VisitsDeps): Visits {
  let key: string | null = null;
  let visitId: string | null = null;
  /** When this tab minted its current visit (wall clock), or null when it adopted it. */
  let mintedAt: number | null = null;
  let lastActivity = 0;
  let lastWrite = -Infinity;
  /** The page's entry applies to the first visit this page starts. */
  let entryUsed = false;
  let listening = false;
  /** Bumped by every identity change, so a lock callback for an old one does nothing. */
  let generation = 0;
  /** A lock-backed mint awaiting its callback, which re-reads the state itself. */
  let minting = false;

  const read = (): VisitState | null => {
    if (!key) return null;
    try {
      return parseState(storage?.getItem(key) ?? null);
    } catch {
      return null;
    }
  };

  const write = (state: VisitState) => {
    if (!key) return;
    try {
      storage?.setItem(key, JSON.stringify(state));
    } catch {
      /* best effort: the visit still stamps this tab */
    }
  };

  const fresh = (s: VisitState | null, now: number): s is VisitState =>
    s !== null && now - s.last_activity_at <= VISIT_IDLE_MS;

  function adopt(id: string) {
    // The page joined a live visit, so its entry belongs to that visit, not a later one.
    entryUsed = true;
    if (id === visitId) return;
    visitId = id;
    mintedAt = null;
    onVisit(id);
  }

  function emitStart(state: VisitState, prev: VisitState | null, reason: VisitReason) {
    let e: VisitEntry = { route: null, source: "direct" };
    if (!entryUsed) {
      entryUsed = true;
      try {
        e = entry();
      } catch {
        /* keep direct */
      }
    }
    record({
      event: "visit.start",
      visit_id: state.visit_id,
      gap_ms: prev ? Math.max(0, Math.round(state.started_at - prev.last_activity_at)) : null,
      entry_route: e.route,
      entry_source: e.source,
      reason,
    });
  }

  function emitMerge(from: string, to: string) {
    record({ event: "visit.merge", from, to });
  }

  /** Start a new visit for the current identity (see the module header for the race rules). */
  function mint(reason: VisitReason, seen: VisitState | null) {
    const now = wallNow();
    const id = newId();
    const mine: VisitState = { visit_id: id, started_at: now, last_activity_at: now };
    visitId = id;
    mintedAt = now;
    lastActivity = now;
    lastWrite = now;
    onVisit(id);
    if (!locks) {
      write(mine);
      emitStart(mine, seen, reason);
      return;
    }
    const gen = generation;
    const lockKey = key;
    let settled = false;
    minting = true;
    const settle = () => {
      if (settled) return;
      settled = true;
      if (gen === generation) minting = false;
      if (gen !== generation || visitId !== id || !lockKey) return;
      const cur = read();
      // A live visit other than this one wins: one another tab minted meanwhile, or (for a
      // load or idle mint) the visit this tab saw expire, which another tab kept alive. An
      // identity switch must start a new visit, so only a visit minted since it decided counts.
      const other =
        cur !== null &&
        cur.visit_id !== id &&
        (reason !== "identity" || cur.visit_id !== seen?.visit_id);
      if (fresh(cur, wallNow()) && other) {
        adopt(cur.visit_id);
        emitMerge(id, cur.visit_id);
        return;
      }
      write({ ...mine, last_activity_at: Math.max(mine.last_activity_at, lastActivity) });
      emitStart(mine, seen, reason);
    };
    try {
      locks.request(`${lockKey}#mint`, settle).catch(() => settle());
    } catch {
      settle();
    }
  }

  /** Reconcile with a state another tab wrote (or that a re-read found). */
  function reconcile(stored: VisitState | null, now: number) {
    if (minting || !fresh(stored, now) || stored.visit_id === visitId) return;
    // Two tabs minted at once without a lock: the lexically smaller id wins. The winner
    // rewrites its state, so the loser hears of it.
    if (
      !locks &&
      visitId &&
      mintedAt !== null &&
      Math.abs(stored.started_at - mintedAt) <= VISIT_RACE_MS
    ) {
      if (visitId < stored.visit_id) {
        write({
          visit_id: visitId,
          started_at: mintedAt,
          last_activity_at: Math.max(now, lastActivity),
        });
        return;
      }
      const loser = visitId;
      adopt(stored.visit_id);
      emitMerge(loser, stored.visit_id);
      return;
    }
    adopt(stored.visit_id);
  }

  const onStorage = (e: StorageEventLike) => {
    if (!key || e.key !== key) return;
    reconcile(parseState(e.newValue), wallNow());
  };

  function listen(on: boolean) {
    if (on === listening || !win) return;
    listening = on;
    try {
      if (on) win.addEventListener("storage", onStorage);
      else win.removeEventListener("storage", onStorage);
    } catch {
      /* best effort */
    }
  }

  return {
    setIdentity(user, org, opts) {
      const next = user ? visitKey(user, org) : null;
      if (next === key) return;
      const switched = key !== null || opts?.switched === true;
      generation += 1;
      minting = false;
      key = next;
      visitId = null;
      mintedAt = null;
      if (!key) {
        listen(false);
        onVisit(null);
        return;
      }
      listen(true);
      const now = wallNow();
      const stored = read();
      // A page load continues a live visit; an account switch always starts a new one.
      if (!switched && fresh(stored, now)) {
        adopt(stored.visit_id);
        lastActivity = now;
        lastWrite = -Infinity;
        this.activity();
        return;
      }
      mint(switched ? "identity" : "load", stored);
    },
    activity() {
      if (!key || !visitId) return;
      const now = wallNow();
      const idle = now - lastActivity > VISIT_IDLE_MS;
      lastActivity = now;
      if (!idle && now - lastWrite < VISIT_WRITE_MS) return;
      const stored = read();
      if (idle && !fresh(stored, now)) {
        mint("idle", stored);
        return;
      }
      if (stored && stored.visit_id !== visitId) {
        reconcile(stored, now);
        if (stored.visit_id !== visitId) return;
      }
      lastWrite = now;
      write({
        visit_id: visitId,
        started_at: stored?.started_at ?? mintedAt ?? now,
        last_activity_at: now,
      });
    },
    stop() {
      generation += 1;
      listen(false);
      const had = visitId !== null;
      key = null;
      visitId = null;
      mintedAt = null;
      if (had) onVisit(null);
    },
    get visitId() {
      return visitId;
    },
  };
}
